import { SpecialNpcDef, RoleAnchor, ScriptTheme } from '../types';

/**
 * 特殊 NPC 附表（2026-10-06 定稿，设计基准 docs/特殊剧本实装设计.md §2.4）：
 * 社交全套（聊天/邀约/送礼/熟遇三池）在 NPCDef 主表（NPCS）；本表只放特殊专用字段。
 * meet.event = 初遇剧情事件（encounter 载体，可长对话/多选项/分支）——两条派发口：
 * ①带 location 的候选＝**到访那侧指名派发**（公园掷偶遇 `EncounterSystem.parkMeetEvent` 只认 loc_park；
 *   商业街进店 `PlotSystem.meetEventAtLocation` 认 shop_cafe 这类店面 ID），两处都按 meet.events 候选序取
 *   第一个"未触发未排队且 trigger 满足（location+calendar）"的；这类 trigger 不进日历扫描
 *   （2026-10-13 茄子定「地点事件必须到访才触发」——扫描侧不看地点，留着等于把「去不去」这把尺收走）；
 * ②不带 location 的候选照旧由日历扫描到点入队。结识由事件内 meetNpcs 完成。
 * ⚠️ 六人（庄文茵/许林辉/穆遥/乔子珊/许澄/罗一鸣）数据批 4 落——许林辉首条已入（2026-10-10）：
 * meet.events 候选二（Y5+ 兜底初遇 FIXED_901_MEET_FAMED）文案待茄子发；favorPlot 好感分段全员暂不写（茄子定）。
 * roleAnchors（2026-10-15）= 特殊 NPC 与某剧本某角色的绑定（exclusive 必演 / fallback 玩家优先、未占到才兜底），
 *   派角与试镜过滤都只走唯一读取口 roleAnchorOf；rival = 竞争型演员，开局实例化进派角池（身份从 NPCDef 派生）。
 */
export const SPECIAL_NPCS: SpecialNpcDef[] = [
    {
        // 庄文茵（影后，绑定《承天》SCRIPT_902）：初遇走《承天》进组钩子（cast）的片场对手戏，
        // 见 docs/特殊剧情/承天.md 事件 1；错过剧情窗口后走主表 firstMeet/firstWarm 轻量兜底。
        npcId: 'npc_zhuangwenyin',
        // 固定咖位 Lv.9（2026-10-21 茄子定原「影坛天后」，重排后该槽=国民女神）：资源型特殊 NPC 不走 NormalNpc 实例，
        // 但固定阵容参评时要读 %咖位名%，所以在此补生涯视图；不参与评选总分。
        rank: 9,
        meet: {
            events: ['FIXED_902_MEET'],
            viaScript: 'SCRIPT_902',
        },
        favorPlot: [],   // 好感分段全员暂不写（2026-10-10 茄子定）
    },
    {
        npcId: 'npc_xulinhui',
        meet: {
            events: ['FIXED_901_MEET'],   // 候选二 FIXED_901_MEET_FAMED（Y5+ 兜底）待文案后追加
            viaScript: 'SCRIPT_901',
        },
        favorPlot: [],   // 好感分段全员暂不写（2026-10-10 茄子定）
    },
    {
        npcId: 'npc_luoyiming',
        meet: {
            events: [],   // 相识锚点事件待茄子发（Y1Q2《凤座》同组相识）
            viaScript: 'SCRIPT_017',
        },
        favorPlot: [],
        rival: {
            rank: 5,
            fame: 1100,   // 竞争型：登场咖位 Lv.5 二线（fame=1100＝新门槛表 Lv.5 线；旧表值 650 随 10-22 改表已过期）
            // 竞争型＝阶段接戏门槛（软限制，规格=docs/特殊人脉/README.md §三；2026-10-24 用户改令）：
            // 人设「爬得急但天花板低」。**⛔ 不给死年份咖位**（原甲案锚点曲线整套已作废，见 README §三 口径沿革）——
            // 她的咖位由「能接到什么档的戏 → 滴灌名气涨多少」自然推出，与普通 NPC 同一条时钟（滴灌 + 过气衰减）。
            auditionLimits: {
                excludedGenres: ['short'],   // 不接短剧（2026-10-20）
                // 阶段门槛（数值定值，导入台账 §7.3 ④‴）：按年段限制她能接的**角色等级**上限。
                // 「爬得急」＝Y3 就放开到 Lv.6；「天花板低」＝Y7 起也只到 Lv.6（Lv.7 及以上永久不开）。
                phaseRoleLevelCaps: [
                    { fromYear: 1, maxRoleLevel: 4 },
                    { fromYear: 3, maxRoleLevel: 6 },
                    { fromYear: 7, maxRoleLevel: 6 },
                ],
            },
        },
        // 角色锚点（2026-10-15）：《凤座》女主柳拂霜——fallback（玩家可试镜，玩家未占到才兜底给她）
        roleAnchors: [
            { scriptId: 'SCRIPT_017', role: '柳拂霜', mode: 'fallback' },
        ],
    },
    {
        // 乔子珊（选秀出身二线小花，竞争型，2026-10-20 导入）。人设与台词见 docs/特殊人脉/乔子珊.md。
        // 初遇暂走主表 firstMeet/firstWarm 轻量兜底——锚点剧情事件待茄子发后再挂进 meet.events。
        npcId: 'npc_qiaozishan',
        meet: {
            events: [],   // 相识锚点事件待茄子发（Y3Q2《偏航降落》同事局）
            viaScript: 'SCRIPT_026',
        },
        favorPlot: [],
        rival: {
            rank: 5,
            fame: 1100,   // 竞争型：登场咖位 Lv.5 二线（fame=1100＝新门槛表 Lv.5 线；旧表值 650 随 10-22 改表已过期，同罗一鸣）
            // 名气收入倍率（单人覆盖，数值定值 2026-10-24）：「起点高但滞涨」给 ×60（罗一鸣走默认 ×30）。
            // 她起点即 Lv.5、Y1~2 还不接戏，×60 是让她在放开戏路后能爬到一线的量级；终局仍不进 Lv.7。
            fameIncomeMult: 60,
            // 竞争型＝阶段接戏门槛（软限制，规格=docs/特殊人脉/README.md §三；2026-10-24 用户改令）：人设「起点高但滞涨」。
            // ⛔ 不给死年份咖位（甲案锚点曲线已作废）；Y1~2 不接戏限制保留，Y3 起放开到 Lv.6（对应《偏航降落》女主夏初 Y3Q2）。
            auditionLimits: {
                excludedGenres: ['short'],           // 不接短剧
                excludedThemes: [
                    ScriptTheme.Period, ScriptTheme.Horror, ScriptTheme.SeriousDrama, ScriptTheme.Military,
                ],   // 年代/恐怖/正剧/军旅（2026-10-21 茄子定）
                blockedYears: [{ from: 1, to: 2 }],  // 第1~2年不接戏
                // 阶段门槛（Chief 2026-10-24 定值口径）：「起点高但滞涨」＝Y1-2 上限 Lv.5、Y3 起上限 Lv.6、
                // Y7 起放开到 Lv.7（Lv.8+ 永久不开）。加最后一档是显式声明天花板，
                // 防「未配档＝不限」的默认放行把她顶到 Lv.8+。
                // ⚠️ 与 `导入台账.md` §7.3 ④‴ 原文（「Y3 起上限 Lv.6（终生不进 Lv.7）」）不一致，
                // 待数值订正权威源；本条按 Chief 派单 + QA 验收口径（乔 ≤Lv.7）落地。
                phaseRoleLevelCaps: [
                    { fromYear: 1, maxRoleLevel: 5 },
                    { fromYear: 3, maxRoleLevel: 6 },
                    { fromYear: 7, maxRoleLevel: 7 },
                ],
            },
        },
        // 角色锚点（2026-10-20 茄子定）：《偏航降落》女主夏初——fallback（玩家可试镜，未占到才兜底给她，
        // 对应「玩家竞角失败后官宣女主是她」的剧情口）
        roleAnchors: [
            { scriptId: 'SCRIPT_026', role: '夏初', mode: 'fallback' },
        ],
    },
    {
        // 黎青（竞争型 · 大器晚成演技派，2026-11-03 导入）。人设与台词见 docs/特殊人脉/黎青.md。
        // 初遇锚点剧情待茄子发；未结识时先走主表 firstMeet/firstWarm 的拍摄周轻量初遇。
        npcId: 'npc_liqing',
        meet: {
            events: [],
        },
        favorPlot: [],
        rival: {
            rank: 1,
            fame: 0,   // 开局 Lv.1（2026-11-03 茄子定值）
            // 阶段接戏门槛（2026-11-03 茄子定值）：长期底部 → Y1~Y7 只接 ≤Lv.3 角色；
            // 爆冷后放开 → Y8 起上限 Lv.7。爆冷跳档本身仍靠接戏滴灌，
            // 「剧情事件显式改 rival fame」的接口待补（见 docs/特殊人脉/README.md §三）。
            auditionLimits: {
                phaseRoleLevelCaps: [
                    { fromYear: 1, maxRoleLevel: 3 },
                    { fromYear: 8, maxRoleLevel: 7 },
                ],
                // 第 8 年起不接短剧、偶像/仙侠/恐怖题材（2026-11-03 茄子定值）；Y1~Y7 仍可接
                phaseExcludedGenres: [
                    { fromYear: 8, genres: ['short'] },
                ],
                phaseExcludedThemes: [
                    { fromYear: 8, themes: [ScriptTheme.Idol, ScriptTheme.Xianxia, ScriptTheme.Horror] },
                ],
            },
        },
    },
];

/**
 * 查某剧本某角色的锚点归属（**全库唯一读取口**——别处再遍历 SPECIAL_NPCS 找锚点即第二把尺）。
 * 命中条件：角色位有名字 且 某特殊 NPC 的 roleAnchors 里列了 (scriptId, role)。
 * 各调用点（试镜过滤 / 派角兜底）都只认这一个出口，⛔ 不复述匹配规则。
 */
export function roleAnchorOf(
    scriptId: string,
    roleName: string | undefined,
): { npcId: string; mode: RoleAnchor['mode'] } | null {
    if (!roleName) return null;
    for (const sp of SPECIAL_NPCS) {
        const hit = sp.roleAnchors?.find(a => a.scriptId === scriptId && a.role === roleName);
        if (hit) return { npcId: sp.npcId, mode: hit.mode };
    }
    return null;
}
